import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Minus, ShieldCheck, Lock, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TIERS, MATRIX, usd } from "@/lib/plans";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";

const FAQ = [
  { q: "How does billing work?", a: "All plans are billed securely through Stripe — monthly or annually. Annual plans save you two months. You can cancel anytime and keep access until the end of your paid period." },
  { q: "Is the Quality Certified badge worth $49.99/month?", a: "One haircut typically covers the membership. Certification is a public trust marker that improves your local ranking, unlocks private client messages and lead access, and gives you the positioning to charge more. Land one or two extra quality clients and it pays for itself." },
  { q: "Can I upgrade or downgrade later?", a: "Yes. Upgrade any time and it takes effect immediately. Downgrades apply at the end of your current billing period." },
  { q: "What's free vs. paid?", a: "Browsing, selected profiles, and featured articles are free. Paid tiers unlock the parts that create income and status: lead access, private messages, certification, better search rank, premium education, and promotion." },
  { q: "Do you offer refunds?", a: "Membership fees are generally non-refundable, but you keep access through your paid period after cancelling. We review duplicate charges and billing errors — see our Refund Policy." },
];

const TIER_MAP = Object.fromEntries(TIERS.map((t) => [t.slug, t]));

function MatrixCell({ v }) {
  if (v === true) return <Check size={18} className="text-[#D4AF37] mx-auto" />;
  if (v === false) return <X size={16} className="text-neutral-600 mx-auto" />;
  if (v === "Add-on") return <span className="text-xs text-neutral-400">Add-on</span>;
  return <span className="text-xs text-neutral-200">{v}</span>;
}

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const choose = async (tier) => {
    const key = annual ? tier.yearlyKey : tier.monthlyKey;
    if (!user) {
      sessionStorage.setItem("pending_plan", key);
      navigate("/register", { state: { plan: key } });
      return;
    }
    try {
      setLoading(tier.slug);
      await startCheckout(key, user.id);
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      {/* header */}
      <div className="text-center max-w-3xl mx-auto">
        <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">Pricing Ladder</p>
        <h1 className="font-display text-4xl md:text-6xl font-bold">Easy to join.<br />Strong reason to stay.</h1>
        <p className="mt-6 text-neutral-300">
          Free to browse. Upgrade for the parts that create trust, leads, and income. Prices grow as the network becomes more valuable.
        </p>

        <div className="mt-8 inline-flex items-center gap-4 border border-white/10 bg-[#111] px-5 py-3">
          <span className={`text-sm ${!annual ? "text-white" : "text-neutral-500"}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} data-testid="pricing-billing-toggle" className="data-[state=checked]:bg-[#D4AF37]" />
          <span className={`text-sm ${annual ? "text-white" : "text-neutral-500"}`}>Annual</span>
          <span className="text-xs font-mono text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5">2 months free</span>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-400">
          <span className="flex items-center gap-1.5"><Lock size={13} className="text-[#D4AF37]" /> Secure Stripe checkout</span>
          <span className="flex items-center gap-1.5"><RefreshCw size={13} className="text-[#D4AF37]" /> Cancel anytime</span>
          <span className="flex items-center gap-1.5"><Zap size={13} className="text-[#D4AF37]" /> Instant access</span>
        </div>
      </div>

      {/* free tier note */}
      <div className="mt-12 border border-white/10 bg-[#0a0a0a] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-xl font-semibold">Free Visitor</h3>
          <p className="text-sm text-neutral-400 mt-1">Browse limited content, view selected profiles, read featured articles, discover Barbistry.</p>
        </div>
        <span className="font-mono text-2xl text-[#D4AF37]">$0</span>
      </div>

      {/* tier cards */}
      <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {TIERS.map((t) => {
          const price = annual ? t.yearly : t.monthly;
          const suffix = annual ? "/yr" : "/mo";
          return (
            <div key={t.slug} data-testid={`pricing-tier-${t.slug}`}
              className={`relative flex flex-col p-7 bg-[#111] border transition-transform duration-300 hover:-translate-y-1 ${
                t.highlight ? "border-[#D4AF37] gold-glow" : "border-white/10 hover:border-[#D4AF37]/40"
              }`}>
              {t.badge && (
                <span className="absolute -top-3 left-7 bg-[#D4AF37] text-black text-xs font-mono uppercase tracking-wider px-3 py-1">{t.badge}</span>
              )}
              <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">{t.audience}</span>
              <h3 className="mt-2 font-display text-2xl font-bold flex items-center gap-2">
                {t.name}
                {t.slug === "certified" && <ShieldCheck size={18} className="text-[#D4AF37]" />}
              </h3>
              <p className="mt-1 text-sm text-neutral-400">{t.tagline}</p>

              <div className="mt-6 flex items-end gap-1">
                <span className="font-display text-4xl font-bold gold-text">{usd(price)}</span>
                <span className="text-neutral-500 mb-1.5 text-sm">{suffix}</span>
              </div>
              {t.freeAvailable && <p className="text-xs text-neutral-500 mt-1">Free tier available</p>}

              <ul className="mt-6 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-neutral-300">
                    <Check size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => choose(t)}
                disabled={loading === t.slug}
                data-testid={`pricing-select-${t.slug}`}
                className={`mt-7 rounded-none h-11 font-semibold ${
                  t.highlight
                    ? "bg-[#D4AF37] text-black hover:bg-[#F0C84B]"
                    : "bg-white/5 text-white hover:bg-[#D4AF37] hover:text-black border border-white/10"
                }`}>
                {loading === t.slug ? "Redirecting…" : `Choose ${t.name}`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* comparison matrix */}
      <div className="mt-24">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Compare</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold">Membership comparison</h2>
        </div>

        <div className="overflow-x-auto border border-white/10">
          <table className="w-full min-w-[760px] text-sm" data-testid="pricing-matrix">
            <thead>
              <tr className="bg-[#0a0a0a] border-b border-white/10">
                <th className="text-left p-4 font-mono text-xs uppercase tracking-wider text-neutral-400 sticky left-0 bg-[#0a0a0a]">Feature</th>
                {MATRIX.columns.map((c) => (
                  <th key={c} className={`p-4 text-center font-display font-semibold ${TIER_MAP[c]?.highlight ? "text-[#D4AF37]" : "text-white"}`}>
                    <div>{TIER_MAP[c]?.name}</div>
                    <div className="text-xs font-mono font-normal text-neutral-500 mt-1">{usd(TIER_MAP[c]?.monthly)}/mo</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX.rows.map((row, ri) => (
                <tr key={row.label} className={`border-b border-white/5 ${ri % 2 ? "bg-white/[0.015]" : ""}`}>
                  <td className="p-4 text-neutral-300 sticky left-0 bg-[#0f0f0f]">{row.label}</td>
                  {row.values.map((v, i) => (
                    <td key={`${row.label}-${MATRIX.columns[i]}`} className={`p-4 text-center ${TIER_MAP[MATRIX.columns[i]]?.highlight ? "bg-[#D4AF37]/[0.04]" : ""}`}>
                      <MatrixCell v={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-neutral-500 mt-4 text-center">All plans billed via secure Stripe checkout. Annual options save you two months. Cancel anytime.</p>
      </div>

      {/* guarantee + FAQ */}
      <div className="mt-24 grid lg:grid-cols-[1fr_1.4fr] gap-10">
        <div className="border border-[#D4AF37]/30 bg-[#111] p-8 h-fit gold-glow">
          <ShieldCheck className="text-[#D4AF37]" size={30} />
          <h3 className="mt-4 font-display text-2xl font-bold">One cut covers it.</h3>
          <p className="mt-3 text-neutral-300 leading-relaxed">
            Certified barbers use trust to charge more and win better clients. If Quality Certified lands you even one or two extra quality clients a month, it more than pays for itself — that's the whole point of the ladder.
          </p>
          <p className="mt-5 text-sm text-neutral-500">No long-term contract. Cancel anytime, keep access through your paid period.</p>
        </div>

        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Questions</p>
          <h2 className="font-display text-3xl font-bold mb-6">Before you join</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="pricing-faq">
            {FAQ.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-neutral-100 hover:text-[#D4AF37] hover:no-underline" data-testid={`faq-trigger-${i}`}>{item.q}</AccordionTrigger>
                <AccordionContent className="text-neutral-400 leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      <div className="mt-16 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold">Ready to get noticed?</h2>
        <p className="mt-3 text-neutral-400">Join the network that turns craft into status, leads, and income.</p>
        <Button onClick={() => choose(TIERS.find((t) => t.slug === "certified"))} data-testid="pricing-cta-certified"
          className="mt-6 rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-10 text-base">
          Get Quality Certified — {usd(annual ? 499 : 49.99)}{annual ? "/yr" : "/mo"}
        </Button>
      </div>
    </div>
  );
}
